import { createContext, useContext, useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { preloadPortfolioData, getBio } from '../firebase';
import { useLoading } from './LoadingContext';
import { logError } from '../utils/error-handler';

const BioContext = createContext(null);

export const BioProvider = ({ children }) => {
  const [bio, setBio] = useState(getBio() || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { finishTask, beginExpectedTasks } = useLoading();

  const loadBio = useCallback(async () => {
    setLoading(true);
    setError(null);
    beginExpectedTasks(['heroBio', 'navbarBio', 'footerBio']);

    try {
      await preloadPortfolioData();
      const bioData = getBio();
      setBio(bioData);
    } catch (err) {
      logError(err, 'BioProvider', {});
      setError(err);
    } finally {
      setLoading(false);
      finishTask('heroBio');
      finishTask('navbarBio');
      finishTask('footerBio');
    }
  }, [beginExpectedTasks, finishTask]);

  const switchLanguageClientSide = useCallback(() => {
    beginExpectedTasks(['heroBio', 'navbarBio', 'footerBio']);

    requestAnimationFrame(() => {
      try {
        const bioData = getBio();
        setBio(bioData);
      } catch (err) {
        logError(err, 'switchLanguageClientSide - Bio', {});
        loadBio();
      } finally {
        finishTask('heroBio');
        finishTask('navbarBio');
        finishTask('footerBio');
      }
    });
  }, [beginExpectedTasks, finishTask, loadBio]);

  useEffect(() => {
    let isMounted = true;
    loadBio();

    const handleLanguageChange = () => {
      if (isMounted) {
        switchLanguageClientSide();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [loadBio, switchLanguageClientSide]);

  const value = useMemo(() => ({ bio, loading, error }), [bio, loading, error]);

  return <BioContext.Provider value={value}>{children}</BioContext.Provider>;
};

export const useBio = () => {
  const context = useContext(BioContext);
  if (!context) {
    throw new Error('useBio must be used within BioProvider');
  }
  return context;
};
